/**
 * RankedSupplierCostList — BLU-599.
 *
 * A ranked list of suppliers for one product category, each with its own
 * landed cost + currency. Rank 1 is the "Primary" (its cost IS the category's
 * standard/landed cost); rank 2 the "Backup"; rank 3+ are "3rd choice" etc.
 * Backups are visibility/planning only in v1 — only the primary feeds valuation.
 *
 * Self-contained + controlled: the parent owns the row array and passes
 * `onChange`. Renders a fragment of `<tr>` sub-rows (+ an "Add supplier" ghost
 * row) so it drops straight into the shared Cost of Goods table and keeps the
 * columns aligned across categories (DESIGN.md §14.1). Built table-agnostic
 * enough that a per-SKU reuse later is mechanical: change the caller, not this.
 *
 * Column contract (must match the caller's <thead> + colgroup colSpan):
 *   1. Rank pill
 *   2. Supplier <Select>
 *   3. Landed cost <Input> + currency (Select when per-row, static suffix when
 *      the page is in apply-to-all mode)
 *   4. Row actions (Make primary / Remove) — revealed on hover / focus-within
 */
import { useEffect, useRef, useState } from 'react';
import { ArrowUp, Plus, Trash2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import type { CostCurrency } from '@threadheads/shared';

export const RANKED_SUPPLIER_COLS = 4;
export const SUPPLIER_EMPTY_VALUE = '__none__';

const COST_CURRENCY_OPTIONS: CostCurrency[] = ['AUD', 'USD', 'CNY', 'EUR', 'GBP'];

/** Sentinel uid meaning "focus the Add-supplier button" rather than a row. */
const ADD_FOCUS = '__add__';

/**
 * One ranked supplier row in edit state. `supplierId` is null while a freshly
 * added row is still unpicked; `unitCost` is a string so the number <Input>
 * can hold an in-progress value (parsed to a number on save by the caller).
 *
 * `uid` is a stable client-only identity (generated on row creation) used for
 * keying + mutation targeting so reorder/add never reuses a DOM node for the
 * wrong row. `rank` is purely the display ordinal, kept contiguous 1..N.
 */
export type SupplierCostRow = {
  uid: string;
  supplierId: string | null;
  rank: number;
  unitCost: string;
  currency: CostCurrency;
};

export type SupplierOption = {
  id: string;
  title: string;
  is_active: boolean;
};

/** 1 → "1st", 2 → "2nd", 3 → "3rd", 4 → "4th"… (English ordinals). */
const ordinal = (n: number): string => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] ?? s[v] ?? s[0]}`;
};

/** Text-bearing rank label — never colour-only (DESIGN.md §16). */
const rankLabel = (rank: number): string => {
  if (rank === 1) return 'Primary';
  if (rank === 2) return 'Backup';
  return `${ordinal(rank)} choice`;
};

/** Reduced-motion guard for the setTimeout-driven flash (DESIGN.md §5). */
const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/** Dense re-rank 1..N following the array's current order. The caller applies
 *  the same contiguity guarantee (inline) when building rows from the backend,
 *  since a stored set may be non-contiguous, e.g. a lone rank-2. */
const resequence = (rows: SupplierCostRow[]): SupplierCostRow[] =>
  rows.map((r, i) => ({ ...r, rank: i + 1 }));

/** Stable client-only row identity. */
const createSupplierRowUid = (): string => crypto.randomUUID();

interface RankedSupplierCostListProps {
  /** Ranked rows for this category (ordered by rank). */
  rows: SupplierCostRow[];
  onChange: (rows: SupplierCostRow[]) => void;
  /** Org supplier options (already sorted by the caller). */
  suppliers: SupplierOption[];
  /** Page-level "apply one currency to every category" mode. */
  applyToAll: boolean;
  /** The single currency applied to every row while `applyToAll` is on. */
  pageCurrency: CostCurrency;
  /** Category label — used to scope aria-labels + announcements. */
  category: string;
  /** Disables every control (e.g. while a save is in flight). */
  disabled?: boolean;
}

export function RankedSupplierCostList({
  rows,
  onChange,
  suppliers,
  applyToAll,
  pageCurrency,
  category,
  disabled = false,
}: RankedSupplierCostListProps) {
  // Focus the supplier picker of a row after add / promote / remove — keyed by
  // the stable uid so a reorder never focuses the wrong DOM node. The `ADD_FOCUS`
  // sentinel moves focus to the "Add supplier" button instead (used when the
  // last row is removed so focus never falls to <body>).
  const triggerRefs = useRef<Map<string, HTMLButtonElement | null>>(new Map());
  const addButtonRef = useRef<HTMLButtonElement | null>(null);
  const [pendingFocusUid, setPendingFocusUid] = useState<string | null>(null);
  // Row (by rank) to flash after an add / make-primary. Cleared on a timer.
  const [flashRank, setFlashRank] = useState<number | null>(null);
  const [announcement, setAnnouncement] = useState('');

  useEffect(() => {
    if (pendingFocusUid == null) return;
    if (pendingFocusUid === ADD_FOCUS) {
      if (addButtonRef.current) {
        addButtonRef.current.focus();
        setPendingFocusUid(null);
      }
      return;
    }
    const el = triggerRefs.current.get(pendingFocusUid);
    if (el) {
      el.focus();
      setPendingFocusUid(null);
    }
  }, [pendingFocusUid, rows]);

  useEffect(() => {
    if (flashRank == null) return;
    if (prefersReducedMotion()) {
      setFlashRank(null);
      return;
    }
    const t = window.setTimeout(() => setFlashRank(null), 300);
    return () => window.clearTimeout(t);
  }, [flashRank]);

  const supplierName = (id: string | null): string =>
    (id ? suppliers.find((s) => s.id === id)?.title : null) ?? 'supplier';

  const patchRow = (uid: string, patch: Partial<SupplierCostRow>) =>
    onChange(rows.map((r) => (r.uid === uid ? { ...r, ...patch } : r)));

  const addRow = () => {
    const nextRank = rows.length + 1;
    const uid = createSupplierRowUid();
    onChange([
      ...rows,
      {
        uid,
        supplierId: null,
        rank: nextRank,
        unitCost: '',
        currency: pageCurrency,
      },
    ]);
    setPendingFocusUid(uid);
    setFlashRank(nextRank);
    setAnnouncement(`Added ${rankLabel(nextRank).toLowerCase()} supplier row for ${category}.`);
  };

  const removeRow = (uid: string) => {
    const index = rows.findIndex((r) => r.uid === uid);
    if (index === -1) return;
    const removed = rows[index];
    const next = resequence(rows.filter((r) => r.uid !== uid));
    onChange(next);
    // Keep keyboard focus in the list: prefer the previous row's supplier
    // trigger, else the new first row, else the Add-supplier button.
    const prev = index > 0 ? rows[index - 1] : null;
    const fallback = next[0];
    if (prev) {
      setPendingFocusUid(prev.uid);
    } else if (fallback) {
      setPendingFocusUid(fallback.uid);
    } else {
      setPendingFocusUid(ADD_FOCUS);
    }
    setAnnouncement(
      `Removed ${supplierName(removed?.supplierId ?? null)} from ${category}. ${next.length} supplier${next.length === 1 ? '' : 's'} remaining.`,
    );
  };

  // Pull a row to the front (rank 1), keeping every other row's relative order,
  // then dense re-rank. For the 2-row default (backup → primary) this is the
  // straight swap the spec calls out; it generalises cleanly for 3+.
  const makePrimary = (uid: string) => {
    const target = rows.find((r) => r.uid === uid);
    if (!target) return;
    const next = resequence([target, ...rows.filter((r) => r.uid !== uid)]);
    onChange(next);
    setFlashRank(1);
    setPendingFocusUid(target.uid);
    setAnnouncement(`${supplierName(target.supplierId)} is now the primary supplier for ${category}.`);
  };

  return (
    <>
      {rows.map((row, index) => {
        const isPrimary = row.rank === 1;
        // Suppliers already chosen in OTHER rows — disabled here so the same
        // supplier can't be picked twice (the DB enforces uniqueness per
        // category; disabling avoids a save-time error).
        const usedElsewhere = new Set(
          rows.filter((_, i) => i !== index).map((r) => r.supplierId).filter(Boolean) as string[],
        );
        const rowCurrency = applyToAll ? pageCurrency : row.currency;

        return (
          <tr
            key={row.uid}
            className={cn(
              'group border-b last:border-b-0',
              flashRank === row.rank && 'motion-safe:animate-fade-in',
            )}
          >
            {/* 1. Rank pill — text-bearing; purple ring/fill for the primary,
                muted for backups. transition-colors carries the state change
                when a row is promoted/demoted. */}
            <td className="px-4 py-2.5 align-middle">
              <span
                className={cn(
                  'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset transition-colors duration-200 motion-reduce:transition-none',
                  isPrimary
                    ? 'bg-threadheads-purple/10 text-threadheads-purple ring-threadheads-purple/30'
                    : 'bg-muted text-muted-foreground ring-border',
                )}
              >
                {rankLabel(row.rank)}
              </span>
            </td>

            {/* 2. Supplier picker. */}
            <td className="px-4 py-2.5 align-middle">
              <Select
                value={row.supplierId ?? SUPPLIER_EMPTY_VALUE}
                disabled={disabled}
                onValueChange={(value) =>
                  patchRow(row.uid, {
                    supplierId: value === SUPPLIER_EMPTY_VALUE ? null : value,
                  })
                }
              >
                <SelectTrigger
                  ref={(el) => {
                    triggerRefs.current.set(row.uid, el);
                  }}
                  className="h-8 w-44 text-xs"
                  aria-label={`${rankLabel(row.rank)} supplier for ${category}`}
                >
                  <SelectValue placeholder="Select supplier" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={SUPPLIER_EMPTY_VALUE}>No supplier</SelectItem>
                  {suppliers.map((supplier) => (
                    <SelectItem
                      key={supplier.id}
                      value={supplier.id}
                      disabled={usedElsewhere.has(supplier.id)}
                    >
                      {supplier.title}
                      {!supplier.is_active && ' (inactive)'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </td>

            {/* 3. Landed cost + currency (to the RIGHT of the number). No
                hardcoded "$" — the currency is always explicit. */}
            <td className="px-4 py-2.5 align-middle">
              <div className="flex items-center gap-1.5">
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={row.unitCost}
                  disabled={disabled}
                  onChange={(e) => patchRow(row.uid, { unitCost: e.target.value })}
                  className="h-8 w-28 tabular-nums"
                  aria-label={`${rankLabel(row.rank)} landed cost for ${category} (${rowCurrency})`}
                />
                {applyToAll ? (
                  <span className="font-mono text-[10px] uppercase text-muted-foreground tabular-nums">
                    {pageCurrency}
                  </span>
                ) : (
                  <Select
                    value={row.currency}
                    disabled={disabled}
                    onValueChange={(value) =>
                      patchRow(row.uid, { currency: value as CostCurrency })
                    }
                  >
                    <SelectTrigger
                      className="h-8 w-[4.75rem] font-mono text-xs uppercase"
                      aria-label={`${rankLabel(row.rank)} currency for ${category}`}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {COST_CURRENCY_OPTIONS.map((code) => (
                        <SelectItem key={code} value={code} className="font-mono uppercase">
                          {code}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </td>

            {/* 4. Row actions — hover / focus-within revealed. Only backups get
                Make-primary + Remove; the primary is always present. */}
            <td className="px-4 py-2.5 align-middle">
              {!isPrimary && (
                <div className="flex items-center gap-0.5 opacity-0 transition-opacity duration-200 group-hover:opacity-100 group-focus-within:opacity-100 motion-reduce:transition-none motion-reduce:opacity-100">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={disabled}
                    onClick={() => makePrimary(row.uid)}
                    className="h-7 gap-1 px-2 text-xs text-muted-foreground hover:text-threadheads-purple"
                    aria-label={`Make ${supplierName(row.supplierId)} the primary supplier for ${category}`}
                  >
                    <ArrowUp className="h-3.5 w-3.5" aria-hidden="true" />
                    Make primary
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={disabled}
                    onClick={() => removeRow(row.uid)}
                    className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                    aria-label={`Remove ${supplierName(row.supplierId)} (${rankLabel(row.rank).toLowerCase()}) from ${category}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                  </Button>
                </div>
              )}
            </td>
          </tr>
        );
      })}

      {/* Add-supplier ghost row — mirrors TrimsCard's add affordance. Also hosts
          the polite live region so add/remove/promote are announced. */}
      <tr>
        <td colSpan={RANKED_SUPPLIER_COLS} className="px-4 py-2">
          <button
            ref={addButtonRef}
            type="button"
            onClick={addRow}
            disabled={disabled}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-md border border-dashed border-muted-foreground/30 px-2.5 py-1 text-xs text-muted-foreground transition-colors duration-200 motion-reduce:transition-none',
              'hover:border-threadheads-purple/40 hover:text-threadheads-purple',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              'disabled:cursor-not-allowed disabled:opacity-50',
            )}
            aria-label={`Add another supplier for ${category}`}
          >
            <Plus className="h-3.5 w-3.5" aria-hidden="true" />
            Add supplier
          </button>
          <span aria-live="polite" className="sr-only">
            {announcement}
          </span>
        </td>
      </tr>
    </>
  );
}
